// A-Reversal — Liquidity Sweep + RSI Divergence reversal detection.
// ═══════════════════════════════════════════════════════════════════════════
// Direct JS port of JarvisSMC_Reversal_Live.pine / jarvis-signal-relay.js's
// computeReversalSignal() — SAME math, same locked-in settings, so all three
// (Pine chart, Telegram relay, this app) agree. Sir's ask (2026-08-23):
// "ani Jarvis ai madhe" — add this to the app too, after the Pine backtest +
// Telegram relay were already built.
//
// 🔴 4h ONLY — the 5-timeframe Pine backtest proved this concept has a real
// edge SOLELY on 4h (15m/30m/45m/1h all showed negative expectancy). This
// module still computes on whatever candles it's given, but callers MUST
// gate on timeframe === '4h' themselves (see LiveMonitor.jsx) — this file
// does not silently refuse a wrong timeframe, same as A_Signal.js doesn't
// second-guess its caller's timeframe choice either.
//
// Settings locked in after backtest A/B testing (2026-08-23, Sir's final
// call): Liquidity Sweep + RSI Divergence (BOTH required) + Candle
// Confirmation (engulfing/pin bar — smoother equity curve, Sir's pick over
// the higher-trade-count/higher-drawdown no-candle-confirm variant). Order
// Block confluence deliberately NOT ported — proven useless in backtest
// (its search anchors to the same pivot the sweep uses, so it was
// structurally almost always true and filtered nothing).
//
// This is a SEPARATE signal from A_Signal.js's trend-following BUY/SELL —
// counter-trend by design (can fire against the current structure trend).
// Never merged into A_Signal.js's own decision.
// ═══════════════════════════════════════════════════════════════════════════

const SWING_LEN    = 50;
const RSI_LEN      = 14;
const MIN_SWEEP_ATR = 0.10;
const MIN_DIV_RSI   = 5;
const SL_ATR_MULT   = 1.0;
const MIN_RR        = 1.5;
const ADX_LEN = 14, ADX_SMOOTH = 14, ADX_THRESH = 20;

function highestHigh(c, endIdx, len) { let h = -Infinity; for (let i = Math.max(0, endIdx - len + 1); i <= endIdx; i++) if (c[i].high > h) h = c[i].high; return h; }
function lowestLow(c, endIdx, len)  { let l = Infinity;  for (let i = Math.max(0, endIdx - len + 1); i <= endIdx; i++) if (c[i].low  < l) l = c[i].low;  return l; }

function rsiSeries(candles, period = RSI_LEN) {
  const n = candles.length;
  const rsi = new Array(n).fill(null);
  if (n < period + 1) return rsi;
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    if (diff >= 0) gainSum += diff; else lossSum -= diff;
  }
  let avgGain = gainSum / period, avgLoss = lossSum / period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < n; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    const gain = diff >= 0 ? diff : 0, loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return rsi;
}

// Walk-forward pivot tracker — same leg-detection as A_Structure.js, but
// also remembers the RSI reading at the moment each pivot was confirmed
// (needed for divergence).
function runReversalPivots(candles, swingLen, rsi) {
  const c = candles;
  if (c.length < swingLen + 2) return { valid: false };
  const BULL = 1, BEAR = 0;
  let legDir = 0, swingHigh = null, swingLow = null;
  for (let i = swingLen; i < c.length; i++) {
    const pivotIdx = i - swingLen;
    const newLegHigh = c[pivotIdx].high > highestHigh(c, i, swingLen);
    const newLegLow  = c[pivotIdx].low  < lowestLow(c, i, swingLen);
    let newLeg = legDir;
    if (newLegHigh) newLeg = BEAR;
    else if (newLegLow) newLeg = BULL;
    if (newLeg !== legDir) {
      if (newLeg === BULL) swingLow  = { price: c[pivotIdx].low,  index: pivotIdx, rsi: rsi[pivotIdx] };
      else                 swingHigh = { price: c[pivotIdx].high, index: pivotIdx, rsi: rsi[pivotIdx] };
      legDir = newLeg;
    }
  }
  if (!swingHigh || !swingLow) return { valid: false };
  return { valid: true, swingHigh, swingLow };
}

// Wilder ATR(14) — same as A_Signal.js's calcATR.
function calcATR(candles, period = 14) {
  const n = candles.length;
  if (n < period + 1) return null;
  const tr = [];
  for (let i = 1; i < n; i++) {
    const c = candles[i], p = candles[i - 1];
    tr.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  let atr = tr.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < tr.length; i++) atr = (atr * (period - 1) + tr[i]) / period;
  return atr;
}

function wilderSum(values, period, startIdx) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = startIdx; i < startIdx + period && i < values.length; i++) sum += values[i];
  out[startIdx + period - 1] = sum;
  for (let i = startIdx + period; i < values.length; i++) out[i] = out[i - 1] - out[i - 1] / period + values[i];
  return out;
}

function runRegime(candles) {
  const c = candles, n = c.length;
  const need = ADX_LEN + ADX_SMOOTH + 2;
  if (n < need) return { ok: false };
  const tr = new Array(n).fill(0), plusDM = new Array(n).fill(0), minusDM = new Array(n).fill(0);
  for (let i = 1; i < n; i++) {
    const upMove = c[i].high - c[i - 1].high, downMove = c[i - 1].low - c[i].low;
    plusDM[i]  = (upMove > downMove && upMove > 0) ? upMove : 0;
    minusDM[i] = (downMove > upMove && downMove > 0) ? downMove : 0;
    tr[i] = Math.max(c[i].high - c[i].low, Math.abs(c[i].high - c[i - 1].close), Math.abs(c[i].low - c[i - 1].close));
  }
  const smoothTR = wilderSum(tr, ADX_LEN, 1), smoothP = wilderSum(plusDM, ADX_LEN, 1), smoothM = wilderSum(minusDM, ADX_LEN, 1);
  const plusDI = new Array(n).fill(null), minusDI = new Array(n).fill(null), dx = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    if (smoothTR[i] != null && smoothTR[i] !== 0) {
      plusDI[i]  = (smoothP[i] / smoothTR[i]) * 100;
      minusDI[i] = (smoothM[i] / smoothTR[i]) * 100;
      const s = plusDI[i] + minusDI[i];
      dx[i] = s !== 0 ? (Math.abs(plusDI[i] - minusDI[i]) / s) * 100 : 0;
    }
  }
  const firstDxIdx = dx.findIndex(v => v !== null);
  if (firstDxIdx === -1 || n - firstDxIdx < ADX_SMOOTH) return { ok: false };
  let sum = 0;
  for (let i = firstDxIdx; i < firstDxIdx + ADX_SMOOTH; i++) sum += dx[i];
  let adxVal = sum / ADX_SMOOTH;
  for (let i = firstDxIdx + ADX_SMOOTH; i < n; i++) adxVal = (adxVal * (ADX_SMOOTH - 1) + dx[i]) / ADX_SMOOTH;
  return { ok: true, adx: adxVal, isTrending: adxVal > ADX_THRESH, threshold: ADX_THRESH };
}

// Engulfing/pin bar on the last candle — same definition as A_Signal.js's
// confirmationCandle().
function confirmationCandle(candles) {
  const n = candles.length;
  if (n < 2) return { bull: false, bear: false };
  const cur = candles[n - 1], prev = candles[n - 2];
  const bodyHi = Math.max(cur.close, cur.open), bodyLo = Math.min(cur.close, cur.open);
  const range = cur.high - cur.low;
  const bullEngulf = cur.close > cur.open && prev.close < prev.open && cur.close >= prev.open && cur.open <= prev.close;
  const bullPin    = range > 0 && (bodyLo - cur.low) >= range * 0.5 && cur.close > (cur.high + cur.low) / 2;
  const bearEngulf = cur.close < cur.open && prev.close > prev.open && cur.close <= prev.open && cur.open >= prev.close;
  const bearPin    = range > 0 && (cur.high - bodyHi) >= range * 0.5 && cur.close < (cur.high + cur.low) / 2;
  return { bull: bullEngulf || bullPin, bear: bearEngulf || bearPin };
}

// candles: array from A-LivePrice.getCandles('4h', ...), oldest → newest
export async function run({ candles = [] }) {
  if (!candles || candles.length < SWING_LEN + RSI_LEN + 5) {
    return { ok: true, action: 'WAIT', reason: 'Sweep/divergence sathi purese candles nahit.', agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }

  const rsi = rsiSeries(candles);
  const pivots = runReversalPivots(candles, SWING_LEN, rsi);
  if (!pivots.valid) {
    return { ok: true, action: 'WAIT', reason: 'Swing pivots ajun confirm zaले nahit.', agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }

  const { swingHigh, swingLow } = pivots;
  const n = candles.length;
  const bar = candles[n - 1];
  const curRSI = rsi[n - 1];
  const atr = calcATR(candles, 14);
  if (!atr || curRSI == null) {
    return { ok: true, action: 'WAIT', reason: 'ATR/RSI ajun ready nahi.', agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }

  const bullSweep = bar.low  < swingLow.price  - atr * MIN_SWEEP_ATR && bar.close > swingLow.price;
  const bearSweep = bar.high > swingHigh.price + atr * MIN_SWEEP_ATR && bar.close < swingHigh.price;

  const bullDivergence = bullSweep && swingLow.rsi  != null && curRSI > swingLow.rsi  + MIN_DIV_RSI;
  const bearDivergence = bearSweep && swingHigh.rsi != null && curRSI < swingHigh.rsi - MIN_DIV_RSI;

  const confirm = confirmationCandle(candles);
  const bullReversal = bullSweep && bullDivergence && confirm.bull;
  const bearReversal = bearSweep && bearDivergence && confirm.bear;

  const regime = runRegime(candles);
  if (!regime.ok) {
    return { ok: true, action: 'WAIT', reason: 'ADX ajun ready nahi.', agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }
  if (!regime.isTrending) {
    return { ok: true, action: 'WAIT', reason: `Choppy market (ADX ${regime.adx.toFixed(1)} <= ${regime.threshold}).`, agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }
  if (!bullReversal && !bearReversal) {
    return { ok: true, action: 'WAIT', reason: 'Ajun sweep + divergence + rejection candle ekत्र jullele nahit.', agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }

  const isBull = bullReversal;
  const sl     = isBull ? swingLow.price - atr * SL_ATR_MULT : swingHigh.price + atr * SL_ATR_MULT;
  const target = isBull ? swingHigh.price : swingLow.price;
  const risk   = Math.abs(bar.close - sl);
  const reward = Math.abs(target - bar.close);
  const rr     = risk > 0 ? reward / risk : 0;
  if (rr < MIN_RR) {
    return { ok: true, action: 'WAIT', reason: `R:R फक्त ${rr.toFixed(2)} (किमान ${MIN_RR} लागतो).`, agent: 'A-Reversal', requiresAI: false, ts: Date.now() };
  }

  return {
    ok: true,
    action: isBull ? 'BUY' : 'SELL',
    entry:  +bar.close.toFixed(2),
    sl:     +sl.toFixed(2),
    target: +target.toFixed(2),
    rr:     +rr.toFixed(2),
    adx:    +regime.adx.toFixed(1),
    isReversal: true, // distinguishes from A_Signal.js's trend-following BUY/SELL
    reason: `${isBull ? 'Bullish' : 'Bearish'} REVERSAL — liquidity sweep + RSI divergence + rejection candle, ADX ${regime.adx.toFixed(1)} trending, R:R ${rr.toFixed(2)}`,
    agent: 'A-Reversal',
    requiresAI: false,
    ts: Date.now(),
  };
}
